import {useIsLoggedIn} from "@/hooks/useUserHooks";
import clsxm from "@/lib/clsxm";
import OnboardingCta from "@/pages/registerCompany/OnboardingCta";
import RegistrationInputField from "@/pages/registerCompany/RegistrationInputField";
import RegistrationEmailField from "@/pages/registerCompany/steps/credentials/RegistrationEmailField";
import RegistrationPasswordField from "@/pages/registerCompany/steps/credentials/RegistrationPasswordField";
import useRegistrationStore from "@/store/registration";
import {useSearch} from "@tanstack/react-router";
import {type FC} from "react";
import {useTranslation} from "react-i18next";

const CredentialsStep: FC<{
  className?: string;
  fieldClassName?: string;
  labelClassName?: string;
  onboardingClassName?: string;
  showCompanyName?: boolean;
  showOnboardingCta?: boolean;
}> = ({
  className,
  fieldClassName,
  labelClassName,
  onboardingClassName,
  showCompanyName = true,
  showOnboardingCta = true,
}) => {
  const {t} = useTranslation("common");
  const isLoggedIn = useIsLoggedIn();
  const setValue = useRegistrationStore(state => state.setValue);
  const domain = useRegistrationStore(state => state.onboardingDomain);
  const registrationEmail = useRegistrationStore(state => state.email);
  const registrationUserId = useRegistrationStore(state => state.userId);
  const urlSearchParams = useSearch({strict: false}) as Record<string, string | undefined>;

  const extraSearchParams = registrationEmail
    ? {
        email: registrationEmail,
        access_token: urlSearchParams.access_token,
        user_id: registrationUserId ?? undefined,
      }
    : undefined;

  return (
    <div className="flex flex-col gap-3">
      {showOnboardingCta && (
        <div className={clsxm("flex w-full flex-col gap-3 px-4 sm:px-20 lg:px-8 xl:px-32", onboardingClassName)}>
          <OnboardingCta
            extraSearchParams={extraSearchParams}
            setValue={value => setValue("onboardingDomain", value)}
            showCreatorAppExit
            value={domain}
          />
          <div className="flex items-center gap-5">
            <div className="bg-primary-light-solid h-px flex-1" />
            <span className="text-neutral-grey-dark text-xs">{t("or")}</span>
            <div className="bg-primary-light-solid h-px flex-1" />
          </div>
        </div>
      )}
      <div className={clsxm(showOnboardingCta && "bg-primary-powder")}>
        <div className={clsxm("flex flex-col gap-5", className)}>
          {showCompanyName && (
            <RegistrationInputField
              storeKey="companyName"
              className={fieldClassName}
              label={t("companyName")}
              labelClassName={labelClassName}
              placeholder={t("companyNamePlaceholder")}
            />
          )}
          <RegistrationEmailField className={fieldClassName} labelClassName={labelClassName} />
          <div className="flex flex-col items-stretch gap-1">
            <label htmlFor="firstName" className={clsxm("text-neutral-grey-dark text-sm font-medium", labelClassName)}>
              {t("register_admin_name_label")}
            </label>
            <div className="flex w-full justify-stretch gap-2">
              <RegistrationInputField className={fieldClassName} storeKey="firstName" placeholder={t("first_name")} />
              <RegistrationInputField className={fieldClassName} storeKey="lastName" placeholder={t("Surname")} />
            </div>
          </div>
          {!isLoggedIn && <RegistrationPasswordField className={fieldClassName} labelClassName={labelClassName} />}
        </div>
      </div>
    </div>
  );
};

export default CredentialsStep;
